import React, { useRef, useEffect } from 'react';
import Type from 'prop-types';
import { usePrevious } from 'react-use';
import { useTranslation } from 'hooks';
import { getRelativeDateTime } from 'utils/date';
import Message from '../Message';
import DateSeparator from '../DateSeparator';
import EmptyState from '../../EmptyState';
import styles from './MessageList.module.css';

const MessageType = {
  DATE: 'date',
  TEXT: 'text',
};

const MessageList = (props) => {
  const { t } = useTranslation();
  const { userId, messages: messagesProp } = props;
  const messageCount = messagesProp.length;
  const hasMessages = messageCount !== 0;
  const bottomRef = useRef(null);
  const currentMessage = messagesProp[messageCount - 1];
  const previousMessage = usePrevious(currentMessage);

  const scrollToBottom = () => {
    bottomRef.current.scrollIntoView();
  };

  useEffect(() => {
    if (currentMessage?.id !== previousMessage?.id) {
      scrollToBottom();
    }
  }, [currentMessage, previousMessage]);

  const insertDates = (messages) => {
    return messages.reduce((newMessages, message, idx) => {
      const messageDate = new Date(message.posted).toDateString();
      let prevMessageDate = messageDate;

      if (idx > 0) {
        const prevMessage = messages[idx - 1];
        prevMessageDate = new Date(prevMessage.posted).toDateString();
      }

      if (idx === 0 || messageDate !== prevMessageDate) {
        return [
          ...newMessages,
          { type: MessageType.DATE, id: idx, posted: message.posted },
          { ...message, type: MessageType.TEXT },
        ];
      }

      return [...newMessages, { ...message, type: MessageType.TEXT }];
    }, []);
  };

  const renderMessageList = () => {
    const allMessages = insertDates(messagesProp);
    allMessages.sort((a, b) => a.posted - b.posted);

    return (
      <div className={styles.container}>
        {allMessages.reduce((elements, message) => {
          if (message.type === MessageType.DATE) {
            elements.push(
              <DateSeparator
                key={message.id}
                date={message.posted}
                formatDate={getRelativeDateTime}
              />,
            );
          }

          if (message.type === MessageType.TEXT) {
            const isMe = message.sender.id === userId;

            elements.push(<Message key={message.id} message={message} isMe={isMe} />);
          }

          return elements;
        }, [])}

        <div key="bottom" ref={bottomRef} className={styles.bottomBorder} />
      </div>
    );
  };

  return (
    <>
      {hasMessages ? (
        renderMessageList()
      ) : (
        <EmptyState title={t('noMessagesYet')} position="absolute" className={styles.emptyState} />
      )}
    </>
  );
};

MessageList.propTypes = {
  messages: Type.arrayOf(Type.shape(Message.propTypes.message)).isRequired,
  userId: Type.string.isRequired,
};

export default MessageList;
